import { ColorUtilitiesService } from '@utilities/color-utilities.service';
import type {
  CustomSeriesOption,
  CustomSeriesRenderItemAPI,
  CustomSeriesRenderItemParams,
  CustomSeriesRenderItemReturn,
  LegendComponentOption,
} from 'echarts';
import {
  IFindingLabel,
  IFindingLabelAnchor,
  IFindingSpan,
  IFindingSpanSeries,
  IGridRect,
  IPositionedFindingLabelAnchor,
  TGraphPoint,
} from './graph-finding-spans.interface';

export const FINDING_SPAN_LABELS_SERIES_ID = 'findingSpanLabels';

const SPAN_WIDTH = 6;
const LABEL_FONT_SIZE = 11;
// Estimated advance of one bold label character; on the generous side so grouped labels never touch.
const LABEL_CHAR_WIDTH = 7;
const LABEL_GAP = 4;
// Above the viewer series (default z 2): its line and area otherwise cover the middle dots.
const SPANS_Z = 3;
// Under the viewer series, so the bands stay a background.
const BANDS_Z = 1;
const BAND_OPACITY = 0.08;

export function sortFindingSpans(items: IFindingSpan[]): IFindingSpan[] {
  return [...items].sort((a, b) => a.start - b.start || a.number - b.number);
}

// Linear interpolation on points sorted by x; outside them the nearest end's y.
export function interpolateY(points: TGraphPoint[], x: number): number {
  if (!points.length) {
    return 0;
  }

  const nextIndex = points.findIndex(([pointX]) => pointX >= x);

  if (nextIndex === 0) {
    return points[0][1];
  }

  if (nextIndex === -1) {
    return points[points.length - 1][1];
  }

  const [x1, y1] = points[nextIndex - 1];
  const [x2, y2] = points[nextIndex];

  return y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
}

// A span's stretch of the line: both ends interpolated, plus every line point strictly between them.
export function getCurveSegment(points: TGraphPoint[], start: number, end: number): TGraphPoint[] {
  return [
    [start, interpolateY(points, start)],
    ...points.filter(([x]) => x > start && x < end),
    [end, interpolateY(points, end)],
  ];
}

// A full-height band behind a span, at least as wide as the span line so findings without duration get one.
function renderFindingSpanBand(
  params: CustomSeriesRenderItemParams,
  api: CustomSeriesRenderItemAPI,
): CustomSeriesRenderItemReturn {
  const { y, height } = params.coordSys as unknown as IGridRect;
  const [startX] = api.coord([api.value(0), 0]);
  const [endX] = api.coord([api.value(1), 0]);
  const width = Math.max(endX - startX, SPAN_WIDTH);

  return {
    shape: { height, width, x: (startX + endX - width) / 2, y },
    style: { fill: api.visual('color') as string, opacity: BAND_OPACITY },
    type: 'rect',
  };
}

function buildFindingSpanBandsSeries(items: IFindingSpan[], color: string): CustomSeriesOption {
  return {
    clip: true,
    data: items.map((item) => [item.start, item.end]),
    encode: { x: [0, 1] },
    id: 'findingSpanBands',
    itemStyle: { color },
    renderItem: renderFindingSpanBand,
    silent: true,
    tooltip: { show: false },
    type: 'custom',
    z: BANDS_Z,
  };
}

// Series data is [span 0 points…, null, span 1 points…, …]: the nulls break the line between spans.
// Each span also gets a dot at its middle: a zero-length stroke draws nothing on canvas, so the dot is what
// keeps a finding without duration visible (on longer spans it disappears inside the line). Labels are
// placed later, per render, by FindingSpansChart.updateLabels. The bands series comes last, so the
// clickable series keep their indexes.
export function buildFindingSpanSeries(
  items: IFindingSpan[],
  segments: TGraphPoint[][],
  color: string,
  name: string,
): IFindingSpanSeries {
  const lineData: (TGraphPoint | null)[] = [];
  const startDataIndexes: number[] = [];
  const markerData = items.map((item, index) => {
    if (index > 0) {
      lineData.push(null);
    }

    startDataIndexes.push(lineData.length);
    lineData.push(...segments[index]);

    const middle = (item.start + item.end) / 2;

    return { name: item.id, value: [middle, interpolateY(segments[index], middle)] };
  });

  return {
    series: [
      {
        color,
        data: lineData,
        emphasis: { disabled: true },
        id: 'findingSpans',
        lineStyle: { cap: 'round', color, join: 'round', width: SPAN_WIDTH },
        name,
        showSymbol: false,
        triggerLineEvent: true,
        type: 'line',
        z: SPANS_Z,
      },
      {
        data: markerData,
        emphasis: { disabled: true },
        id: 'findingSpanMarkers',
        itemStyle: { color, opacity: 1 },
        symbolSize: SPAN_WIDTH,
        tooltip: { show: false },
        type: 'scatter',
        z: SPANS_Z,
      },
      {
        // Filled after each render by FindingSpansChart.updateLabels: grouping needs pixel positions.
        animation: false,
        data: [],
        emphasis: { disabled: true },
        id: FINDING_SPAN_LABELS_SERIES_ID,
        itemStyle: { color: 'transparent' },
        label: {
          color,
          distance: SPAN_WIDTH,
          fontSize: LABEL_FONT_SIZE,
          fontWeight: 'bold',
          position: 'top',
          show: true,
        },
        symbolSize: 1,
        tooltip: { show: false },
        type: 'scatter',
        z: SPANS_Z + 1,
      },
      buildFindingSpanBandsSeries(items, color),
    ],
    startDataIndexes,
  };
}

export function getFindingSpansLegend(name: string): LegendComponentOption {
  return {
    data: [{ icon: 'roundRect', name }],
    itemHeight: SPAN_WIDTH,
    itemWidth: 16,
    right: 0,
    selectedMode: false,
    textStyle: { color: ColorUtilitiesService.shikensoPurpleBase, fontSize: 12 },
    top: 0,
  };
}

// Where a span's label goes at the current zoom: the middle of the span's visible part, on the line, so a
// long span zoomed into keeps its label on screen. Null when the span is entirely off screen.
export function getVisibleLabelAnchor(
  span: IFindingSpan,
  segment: TGraphPoint[],
  [visibleMin, visibleMax]: [number, number],
): IFindingLabelAnchor | null {
  const start = Math.max(span.start, visibleMin);
  const end = Math.min(span.end, visibleMax);

  if (start > end) {
    return null;
  }

  const middle = (start + end) / 2;

  return { id: span.id, number: span.number, value: [middle, interpolateY(segment, middle)] };
}

// `4, 5` for two neighbours, `4–8` for a run of three or more, so dense groups stay short.
export function formatFindingLabelNumbers(numbers: number[]): string {
  const runs: number[][] = [];

  for (const number of [...numbers].sort((a, b) => a - b)) {
    const run = runs[runs.length - 1];

    if (run && number === run[run.length - 1] + 1) {
      run.push(number);
    } else {
      runs.push([number]);
    }
  }

  return runs.map((run) => (run.length > 2 ? `${run[0]}–${run[run.length - 1]}` : run.join(', '))).join(', ');
}

// Every finding keeps a visible number: labels that would overlap at the current zoom are merged into one
// label centred over the group, repeated until none overlap.
export function groupFindingLabels(anchors: IPositionedFindingLabelAnchor[]): IFindingLabel[] {
  const groups: IPositionedFindingLabelAnchor[][] = [];

  for (const anchor of [...anchors].sort((a, b) => a.pixelX - b.pixelX)) {
    groups.push([anchor]);

    while (groups.length > 1 && labelsOverlap(groups[groups.length - 2], groups[groups.length - 1])) {
      const last = groups.pop()!;
      groups.push([...groups.pop()!, ...last]);
    }
  }

  return groups.map((group) => {
    const [first] = group;
    const last = group[group.length - 1];
    const lowest = group.reduce((lowestAnchor, anchor) =>
      anchor.number < lowestAnchor.number ? anchor : lowestAnchor,
    );

    return {
      id: lowest.id,
      text: formatFindingLabelNumbers(group.map((anchor) => anchor.number)),
      value: [(first.value[0] + last.value[0]) / 2, Math.max(...group.map((anchor) => anchor.value[1]))],
    };
  });
}

// Line clicks carry no dataIndex in ECharts, only where they hit: the span containing that x (the shortest
// when spans overlap), else the nearest one, since a click on a round line cap lands just outside its span.
export function findFindingSpanIndexAt(items: IFindingSpan[], x: number): number {
  let bestIndex = -1;
  let bestDistance = Infinity;
  let bestLength = Infinity;

  items.forEach((item, index) => {
    const distance = Math.max(item.start - x, x - item.end, 0);
    const length = item.end - item.start;

    if (distance < bestDistance || (distance === bestDistance && length < bestLength)) {
      bestIndex = index;
      bestDistance = distance;
      bestLength = length;
    }
  });

  return bestIndex;
}

// Total x-axis length the spans cover, overlapping parts counted once.
export function getFindingSpansCoverage(items: Pick<IFindingSpan, 'start' | 'end'>[]): number {
  let total = 0;
  let coveredUntil = -Infinity;

  for (const { start, end } of [...items].sort((a, b) => a.start - b.start)) {
    const from = Math.max(start, coveredUntil);

    if (end > from) {
      total += end - from;
      coveredUntil = end;
    }
  }

  return total;
}

function labelsOverlap(left: IPositionedFindingLabelAnchor[], right: IPositionedFindingLabelAnchor[]): boolean {
  return getLabelBounds(left)[1] + LABEL_GAP > getLabelBounds(right)[0];
}

function getLabelBounds(group: IPositionedFindingLabelAnchor[]): [number, number] {
  const center = (group[0].pixelX + group[group.length - 1].pixelX) / 2;
  const halfWidth = (formatFindingLabelNumbers(group.map((anchor) => anchor.number)).length * LABEL_CHAR_WIDTH) / 2;

  return [center - halfWidth, center + halfWidth];
}
